import React from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiCheckCircle, FiCode, FiAlertCircle } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import Carousel from '../components/Carousel';
import ImageModal from '../components/ImageModal';

const QuickStart = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const steps = [
    {
      title: "Installation Options",
      description: "Ana Security is available in two formats:",
      options: [
        {
          title: "Web Application",
          description: "Access Ana Security through your web browser:",
          steps: [
            "Visit security-dev.Ana.ai",
            "Sign up or log in to your account",
            "Start scanning your projects immediately"
          ]
        },
        {
          title: "IDE Integration",
          description: "Integrate Ana Security directly in your development environment:",
          steps: [
            "Install the Ana Security plugin",
            "Configure your project settings",
            "Run scans directly from your IDE"
          ]
        }
      ]
    },
    {
      title: "Getting Started",
      description: "Follow these steps to start securing your applications:",
      steps: [
        "Connect your first project or repository",
        "Run your initial security scan (start with SCA)",
        "Review the security findings and prioritize fixes",
        "Use ANA Actions to automatically fix issues",
        "Set up continuous scanning for ongoing protection"
      ]
    }
  ];

  const bestPractices = [
    {
      icon: <FiCode />,
      title: "Start with SCA",
      description: "Begin with Software Composition Analysis for quick wins",
      points: [
        "Easiest security module to get started",
        "Identifies vulnerable dependencies",
        "Often provides automated fixes"
      ]
    },
    {
      icon: <FiCheckCircle />,
      title: "Prioritize by Severity",
      description: "Focus on Critical and High severity issues first",
      points: [
        "Address immediate security risks",
        "Use the severity-based dashboard view",
        "Track progress over time"
      ]
    },
    {
      icon: <FiAlertCircle />,
      title: "Use ANA Actions",
      description: "Leverage automated fixes for faster remediation",
      points: [
        "Look for orange 'Fix' buttons",
        "Review automated pull requests",
        "Merge fixes after testing"
      ]
    }
  ];

  const nextSteps = [
    {
      title: "SCA Scanning",
      description: "Deep dive into dependency scanning",
      href: "/sca"
    },
    {
      title: "ANA Actions",
      description: "Learn about automated fixes",
      href: "/ana-actions"
    },
    {
      title: "Integrations",
      description: "Connect with your workflow",
      href: "/integrations"
    }
  ];

  const cardClasses = `
    group p-6 rounded-lg 
    bg-light-card dark:bg-dark-card
    backdrop-blur-sm
    border border-light-border dark:border-dark-border
    transition-all duration-200 
    hover:border-primary-500/30 dark:hover:border-primary-500/30
    hover:shadow-lg hover:shadow-primary-500/5 dark:hover:shadow-primary-500/10
    h-full
  `;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Quick Start Guide" />
        
        <div className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          Get up and running with Ana Security in under 5 minutes. Perfect for developers who want to start scanning immediately.
        </div>

        {/* 3-Step Quick Start */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          🚀 3-Step Quick Start
        </div>
        
        <div className="space-y-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Login & Access Dashboard
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Go to <code className="bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded">https://security.openana.ai/</code> and sign in with your credentials.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Run Your First SCA Scan
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Click "SCA" in the sidebar → "New Scan" → Select your project → Wait for results.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Fix Issues with ANA Actions
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Look for orange "Fix" buttons → Click to create automated pull requests → Review and merge.
              </p>
            </div>
          </div>
        </div>

        {/* Installation Steps */}
        {steps.map((section, index) => (
          <div key={index} className="mb-8">
            <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              {section.title}
            </div>
            <div className="text-xl text-gray-600 dark:text-gray-400 mb-6">
              {section.description}
            </div>

            {section.options ? (
              <>
                {/* Desktop: Grid layout */}
                <div className="hidden md:grid md:grid-cols-2 gap-6">
                  {section.options.map((option, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className={cardClasses}>
                        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                          {option.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          {option.description}
                        </p>
                        <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
                          {option.steps.map((step, stepIdx) => (
                            <li key={stepIdx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile: Carousel */}
                <div className="md:hidden">
                  <Carousel showDots={true}>
                    {section.options.map((option, idx) => (
                      <div key={idx} className="px-2">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: idx * 0.1 }}
                        >
                          <div className={cardClasses}>
                            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                              {option.title}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              {option.description}
                            </p>
                            <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
                              {option.steps.map((step, stepIdx) => (
                                <li key={stepIdx}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        </motion.div>
                      </div>
                    ))}
                  </Carousel>
                </div>
              </>
            ) : (
              <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
                {section.steps.map((step, stepIdx) => (
                  <li key={stepIdx}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        ))}

        {/* Best Practices */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Best Practices for Getting Started
        </div>
        
        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-8">
          {bestPractices.map((practice, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={cardClasses}>
                <div className="text-2xl text-primary-500 mb-3 transition-transform group-hover:scale-110">
                  {practice.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {practice.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {practice.description}
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {practice.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden mb-8">
          <Carousel showDots={true}>
            {bestPractices.map((practice, index) => (
              <div key={index} className="px-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={cardClasses}>
                    <div className="text-2xl text-primary-500 mb-3 transition-transform group-hover:scale-110">
                      {practice.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {practice.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {practice.description}
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {practice.points.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Success Message */}
        <div className="text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              🎉 You're All Set!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              That's it! You're now scanning for security vulnerabilities and can fix them automatically.
            </p>
            
            {/* Desktop: Grid layout */}
            <div className="hidden md:grid md:grid-cols-2 gap-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/dashboard" 
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30"
                >
                  <FiPlay className="w-5 h-5" />
                  View Dashboard
                </a>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/" 
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <FiCheckCircle className="w-5 h-5" />
                  Full Getting Started Guide
                </a>
              </motion.div>
            </div>

            {/* Mobile: Carousel */}
            <div className="md:hidden">
              <Carousel showDots={true}>
                <div className="px-2">
                  <a 
                    href="/dashboard" 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30 w-full"
                  >
                    <FiPlay className="w-5 h-5" />
                    View Dashboard
                  </a>
                </div>
                <div className="px-2">
                  <a 
                    href="/" 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full"
                  >
                    <FiCheckCircle className="w-5 h-5" />
                    Full Getting Started Guide
                  </a>
                </div>
              </Carousel>
            </div>
          </motion.div>
        </div>

        {/* Next Steps */}
        <div className="text-xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
          Ready for More?
        </div>
        
        <div className="text-xl text-gray-600 dark:text-gray-400 mb-6">
          Explore our comprehensive security modules and learn how to build a complete security program.
        </div>
        
        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {nextSteps.map((step, index) => (
            <motion.div key={index} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <a 
                href={step.href} 
                className={cardClasses}
              >
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h4>
                <p className="text-gray-600 dark:text-gray-400">{step.description}</p>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden">
          <Carousel showDots={true}>
            {nextSteps.map((step, index) => (
              <div key={index} className="px-2">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a 
                    href={step.href} 
                    className={cardClasses}
                  >
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h4>
                    <p className="text-gray-600 dark:text-gray-400">{step.description}</p>
                  </a>
                </motion.div>
              </div>
            ))}
          </Carousel>
        </div>
      </motion.div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
      />
    </div>
  );
};

export default QuickStart;
